import groupby from 'lodash/groupBy';
import orderby from 'lodash/orderBy';
import {DateTime} from 'luxon';
import {createSelector} from '@reduxjs/toolkit';

import {ORDER_STATES} from '../constants/index.js';
import {apiSlice} from '@explorer/api/apiSlice';
import {getFolderIdFromOrderName, orderIsFolder} from '@explorer/util/folders';
import {getOrders} from './orders.js';

export const getFoldersStates = createSelector([getOrders], orders => {
  const folderStates = {};

  if (orders.length) {
    for (let i = 0, ii = orders.length; i < ii; i++) {
      const order = orders[i];
      if (orderIsFolder(order)) {
        const folderId = getFolderIdFromOrderName(order.name);
        if (!folderStates[folderId]) {
          folderStates[folderId] = [order.state];
        } else if (folderStates[folderId].indexOf(order.state) < 0) {
          folderStates[folderId].push(order.state);
        }
      }
    }
  }
  return folderStates;
});

export const getFoldersWithActiveOrders = createSelector(
  [getFoldersStates],
  folderStates => {
    const loadingFolders = {};

    for (const key in folderStates) {
      const folder = folderStates[key];
      loadingFolders[key] =
        folder.indexOf(ORDER_STATES.RUNNING) >= 0 ||
        folder.indexOf(ORDER_STATES.QUEUED) >= 0;
    }

    return loadingFolders;
  }
);

export const getSelectedFolderId = state => state.folders.selectedFolderId;

const selectFolderResult = apiSlice.endpoints.getFolder.select();

export const getSelectedFolder = createSelector(
  selectFolderResult,
  folderResult => {
    return folderResult?.data ?? {};
  }
);

/* Folders Result/Scenes List */

export const getFolderLoadingState = createSelector(
  [getFoldersWithActiveOrders, getSelectedFolderId],
  (loadingFolders, getSelectedFolderId) =>
    loadingFolders[getSelectedFolderId] === true
);

export const getScenesSortDirection = state =>
  state.sidebar.scenesSortDirection;

const folderContentsById = state => state.folders.folderContentsById;

export const getResults = createSelector(
  [folderContentsById],
  folderContentsById => Object.values(folderContentsById)
);

export const getContentItemCount = createSelector(
  [getResults],
  results => results.map(val => val.properties.items).flat().length
);

export const getSortedResults = createSelector(
  [getResults, getScenesSortDirection],
  (itemGroups, sortDirection) => {
    const things = orderby(
      itemGroups,
      itemGroup =>
        Date.parse(itemGroup.properties.items[0].properties.acquired),
      sortDirection
    );
    return things;
  }
);

export const getActiveResult = state => state.folders.activeResult;

export const getActiveResultItemsGroupedByCollect = createSelector(
  [getActiveResult],
  activeResult => {
    // Group by the Planet strip_id or by satellite_id
    // NOTE: In case folders ever uses data without strip_id, like in Search Results
    const groupByKey = item =>
      item.properties.strip_id
        ? item.properties.strip_id
        : item.properties.satellite_id;

    const groups = groupby(activeResult.properties.items, groupByKey);

    return Object.keys(groups).map(key => ({
      id: key,
      properties: {
        acquired: groups[key][0].properties.acquired,
        item_type: groups[key][0].properties.item_type,
        items: groups[key],
      },
    }));
  }
);

export const getSortedActiveResultItems = createSelector(
  [getActiveResultItemsGroupedByCollect, getScenesSortDirection],
  (itemGroups, sortDirection) => {
    const sortedGroups = orderby(
      itemGroups,
      itemGroup => {
        return Date.parse(itemGroup.properties.items[0].properties.acquired);
      },
      sortDirection
    );

    return sortedGroups;
  }
);

const selectViewResults = state => state.folders.viewResults;

export const getSelectedResults = createSelector(
  [selectViewResults, folderContentsById],
  (viewResults, folderContentsById) =>
    viewResults.map(id => folderContentsById[id])
);

export const getSelectedItems = state => state.folders.viewItems;

export const getSelectedFolderItems = createSelector(
  [getSelectedResults, getSelectedItems],
  (selectedResults, selectedItems) => {
    const allItems = selectedResults
      .map(result => result.properties.items)
      .flat();

    return allItems.filter(item => selectedItems.includes(item.id));
  }
);

export const getSelectedResultItemType = createSelector(
  [getSelectedResults],
  selectedResults => {
    return selectedResults[0]?.properties.item_type || '';
  }
);

// Get the First and Last date for selected results shown on the map
export const getSelectedFolderResultsDateRange = createSelector(
  [getSelectedResults],
  selectedResults => {
    const dates = selectedResults.map(result =>
      DateTime.fromISO(result.properties.acquired, {zone: 'utc'})
    );

    if (dates.length) {
      const first = DateTime.min(...dates);
      const last = DateTime.max(...dates);
      if (first === last) {
        return [first.toISO()];
      } else {
        return [first.toISO(), last.toISO()];
      }
    }

    return [];
  }
);
